<template>
  <div>
    <v-card class='mx-2' outlined>
      <v-card-title>
        <v-container fluid>
          Camera Status
          <v-row>
            <v-col cols="12" sm="2" md="1" class="mt-2">
              <span><h5>Filters:</h5></span>
            </v-col>
            <v-col cols="12" sm="6" md="2" xl="1">
              <v-select
                v-model="filterGateways"
                @change="saveFilters"
                :items="['true','false']"
                label="GW Connection"
                clearable
                dense
              ></v-select>
            </v-col>
            <v-col cols="12" sm="6" md="2" xl="1">
              <v-select
                v-model="filterCloudDays"
                @change="saveFilters"
                :items="['7','14','30','45','60','90','180', '365']"
                label="Cloud days at least"
                clearable
                dense
              ></v-select>
            </v-col>
            <v-col cols="12" sm="6" md="2" xl="1">
              <v-select
                v-model="filterEnable"
                @change="saveFilters"
                :items="['true','false']"
                label="Enabled"
                clearable
                dense
              ></v-select>
            </v-col>
            <v-col cols="12" sm="6" md="3" xl="3">
              <v-select
                v-model="filterConnected"
                @change="saveFilters"
                :items="connectedStates"
                multiple
                label="Device Connected"
                dense
              ></v-select>
            </v-col>
            <v-spacer></v-spacer>
            <v-col cols="12" sm="6" md="2" xl="2">
              <v-text-field
                v-model="search"
                append-icon="mdi-magnify"
                label="Search (incl hidden cols)"
                single-line
                hide-details
                clearable
                dense
              ></v-text-field>
            </v-col>
          </v-row>
        </v-container>
      </v-card-title>
      <v-data-table v-model="selected" 
        :headers="headers" 
        :items="cameras" 
        :search="search" 
        show-select
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        @update:sort-by="saveSort"
        @update:sort-desc="saveSort"
        :items-per-page='parseInt("-1")'
        no-data-text="No Documents">
        <template v-slot:top>
          <v-toolbar flat color="white">
            <v-btn 
              color="primary" 
              class="mt-n6"
              @click="editColumns">
              Edit Cols
            </v-btn>
            <v-dialog 
              v-model="showEditColDialog" 
              max-width="50%">
              <v-card class="px-4">
                <v-card-title>
                  <span class="headline">Select Columns to Show</span>
                </v-card-title>
                <v-card-text>
                  <v-container>
                  <v-select
                    v-model="selectedColumns"
                    :items="headers"
                    multiple
                    @change="changeColumns">
                  </v-select>
                  </v-container>
                </v-card-text>
                <v-card-actions>
                  <div class="flex-grow-1"></div>
                  <v-btn color="primary" @click="editColumns">Ok</v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
            <v-spacer></v-spacer>
            <v-btn 
              color="primary" 
              class="mt-n6"
              :disabled='selected.length===0'
              @click="exportCameras">
              <v-icon left>
                fa-file-excel-o
              </v-icon>
                Export
            </v-btn>
            <v-menu>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  color="primary"
                  class="mx-2 mt-n6"
                  v-bind="attrs"
                  v-on="on"
                  :disabled='selected.length===0'>
                <v-icon left>
                  fa-refresh
                </v-icon>
                  Motion
                </v-btn>
              </template>
              <v-list>
                <v-list-item
                  v-for="(item, index) in eventDurations"
                  :key="index"
                  @click="updateMotion(item.val)">
                  <v-list-item-title>
                    {{ item.str }}
                  </v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
            <v-menu>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  color="primary"
                  class="mt-n6"
                  v-bind="attrs"
                  v-on="on"
                  :disabled='selected.length===0'>
                <v-icon left>
                  fa-refresh
                </v-icon>
                  Streams
                </v-btn>
              </template>
              <v-list>
                <v-list-item
                  v-for="(item, index) in eventDurations"
                  :key="index"
                  @click="updateStreamEvents(item.val)">
                  <v-list-item-title>
                    {{ item.str }}
                  </v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
            <v-btn 
              color="primary" 
              class="mx-2 mt-n6"
              :disabled='selected.length===0'
              @click="updateCloudStats">
              <v-icon left>
                fa-refresh
              </v-icon>
                Stats
            </v-btn>
          </v-toolbar>
        </template>
        <template v-slot:item.devGwSerNum="{ item }">
          <span v-html=item.devGwSerNum :style="getGwColor(item.devGwConnected)"></span>
        </template>
        <template v-slot:item.vsEnabled="{ item }">
          <span v-html=item.vsEnabled :style="getDevEnabledColor(item.devGwConnected, item.vsEnabled)"></span>
        </template>
        <template v-slot:item.devConnected="{ item }">
          <span v-html=item.devConnected :style="getDevConnectedColor(item.devGwConnected, item.vsEnabled, item.devConnected)"></span>
        </template>
        <template v-slot:item.vsMpConnectedStatus="{ item }">
          <v-tooltip left>
            <template v-slot:activator="{ on }">
                  <v-chip
                    :color="getStreamColor(item.vsMpConnectedStatus)"
                    :style="item.devGwConnected && item.vsEnabled? '' : 'opacity: 0.3'"
                    dark
                    small
                    v-on="on"
                  >
                    {{ item.vsMpConnectedStatus }}
                  </v-chip>
            </template>
            <span v-html=item.tooltip></span>
          </v-tooltip>
        </template>
       </v-data-table>
    </v-card>
  </div>
</template>

<script>

import { saveAs } from 'file-saver';
import { DateTime } from "luxon";

export default {
  name: "Cameras",
  props: {
    app: Object,
    setProgressVal: Function,
    setProgressLinear: Function,
  },
  data: () => ({
    sortBy: "devGwSerNum",
    sortDesc: false,
    search: '',
    devices: [],
    cameras: [],
    events: [],
    selected: [],
    selectedColumns: [],
    showEditColDialog: false,
    lastMotionCtr: 0,
    streamEventIdx: 0,
    eventDuration: 0,   // shared between motion and stream events queries
    cloudStatsIdx: 0,
    filterGateways: '',
    filterCloudDays: 0,
    filterEnable: '',
    //Read from eventTypes(where: {type: {_eq: \"DeviceConnection\"}}) {statusValues {value}}
    connectedStates: ['Connected','Connecting','Disconnected','InvalidCredentials','NotSupported','Rebooting'],
    filterConnected: ['Connected','Connecting','Disconnected','InvalidCredentials','NotSupported','Rebooting'],
    eventDurations: [
      {'str':'Past 1 hr', 'val': 1},
      {'str':'Past 2 hrs', 'val': 2},
      {'str':'Past 4 hrs', 'val': 4},
      {'str':'Past 6 hrs', 'val': 6},
      {'str':'Past 12 hrs', 'val': 12},
      {'str':'Past 24 hrs', 'val': 24},
      {'str':'Past 7 days', 'val': 168},
      {'str':'Past 30 days', 'val': 720}
    ],
  }),

  created() {
    console.log('Cameras created')
  },

  mounted() {
    console.log('Cameras mounted')
    this.initialize();
    let item = sessionStorage.getItem('camerasGatewayFilter');
    if (item) this.filterGateways = item;
    item = sessionStorage.getItem('camerasCloudDaysFilter');
    if (item) this.filterCloudDays = item;
    item = sessionStorage.getItem('camerasEnableFilter');
    if (item) this.filterEnable = item;
    item = sessionStorage.getItem('camerasConnectedFilter');
    if (item) this.filterConnected = item.split(",");
    item = sessionStorage.getItem("camerasSortBy"); 
    if (item) this.sortBy = item;
    item = sessionStorage.getItem("camerasSortDesc"); 
    if (item) this.sortDesc = item === 'true';
  },

  computed: {
    headers() {
      return [
      {
        text: 'Gateway',
        align: 'left',
        value: 'devGwSerNum',
      },
      {
        text: 'Cust Place',
        align: 'left',
        value: 'custPlace'
      },
      {
        text: 'Name',
        align: 'left',
        value: 'devName',
      },
      {
        text: 'IP Address',
        align: 'left',
        value: 'devAddress'
      },
      {
        text: 'Make',
        align: 'left',
        value: 'devMake'
      },
      {
        text: 'Model',
        align: 'left',
        value: 'devModel'
      },
      {
        text: 'Firmware',
        align: 'left',
        value: 'devFirmware'
      },
      // {
      //   text: 'vsId',    // videoSourceID is usefule for debugging
      //   align: 'left',
      //   value: 'vsId'
      // },
      {
        text: 'Cloud Storage',
        align: 'left',
        value: 'vsCloudDays',
        filter: value => {
          if (!this.filterCloudDays) return true
          return value >= parseInt(this.filterCloudDays)
        },
      },
      {
        text: 'Device Enabled',
        align: 'left',
        value: 'vsEnabled',
        filter: value => {
          if (!this.filterEnable) return true
          return value == (this.filterEnable == 'true')
        },
      },
      {
        text: 'Device Connected',
        align: 'left',
        value: 'devConnected',
        filter: (value, search, item) => {
          const devFilterDisabled = this.filterConnected.length == this.connectedStates.length;
          const gwFilterDisabled = !this.filterGateways;
          if (devFilterDisabled && gwFilterDisabled) {
            return true
          }
          const gwFilter = item.devGwConnected == (this.filterGateways == 'true');
          if (devFilterDisabled) return gwFilter;
          const found = this.filterConnected.findIndex(str => {
            return str === value;
          })
          const devFilter = found >= 0;
          if( gwFilterDisabled) return devFilter;
          return gwFilter && devFilter;
        },
      },
      {
        text: 'Streams',
        align: 'left',
        value: 'vsMpConnectedStatus',
      },
      {
        text: 'Resolutions',
        align: ' d-none',
        value: 'resolutions'
      },
      {
        text: 'Last Motion',
        align: 'left',
        value: 'lastMotionDt'
      },
      {
        text: 'Stream Events',
        align: 'left',
        value: 'streamEventCount'
      },
      {
        text: 'Cloud GB',
        align: 'left',
        value: 'cloudGB'
      },
      {
        text: 'Orphaned GB',
        align: ' d-none',
        value: 'orphanedGB'
      },
    ]},
  },

  methods: {
    initialize() {
      this.headers.forEach(header => {
        if (header.align !== " d-none") {
          this.selectedColumns.push(header.value);
        }
      }) 
      this.getCameras();
    },

    getGwColor (connected) {
      if (connected == true) return 'color:black'
      else return 'color:red'
    },

    getDevEnabledColor (gwConn, enabled) {
      let ret = '';
      if (enabled == true) ret = 'color:black'
      else ret = 'color:blue'
      if (!gwConn) {
        ret += ';opacity: 0.3';
      }
      return ret;
    },

    getDevConnectedColor (gwConn, enabled, connected) {
      let ret = '';
      if (enabled == true && connected === "Connected") ret = 'color:green'
      else if (enabled == true && connected === "Connecting") ret = 'color:orange'
      else if (enabled == true && connected === "Disconnected") ret = 'color:red'
      else ret = 'color:blue'
      if (!gwConn) {
        ret += ';opacity: 0.3';
      }
      return ret;
    },

    getStreamColor (connString) {
      if (connString == "Ok") return 'green'
      else if (connString == "Add") return 'blue'
      else if (connString == "Alert") return 'orange'
      else if (connString == "ch17918") return 'pink'
      else return 'red'
    },

    editColumns() {
      this.showEditColDialog = !this.showEditColDialog;
    },

    changeColumns() {
      this.headers.forEach(header => {
        header.align = " d-none";
      })
      this.selectedColumns.forEach(item => {
        let headerIdx = this.headers.findIndex(obj => {
          return obj.value === item;
        })
        if (headerIdx >= 0) {
          this.headers[headerIdx].align = "left";
        }
      })
    },

    saveSort() {
      sessionStorage.setItem("camerasSortBy", this.sortBy); 
      sessionStorage.setItem("camerasSortDesc", this.sortDesc); 
    },

    saveFilters() {
      if (this.filterGateways?.length) {
        sessionStorage.setItem('camerasGatewayFilter', this.filterGateways);
      } else {
        sessionStorage.setItem('camerasGatewayFilter', "");
      }
      if (this.filterCloudDays) {
        sessionStorage.setItem('camerasCloudDaysFilter', this.filterCloudDays);
      } else {
        sessionStorage.setItem('camerasCloudDaysFilter', 0);
      }
      if (this.filterEnable?.length) {
        sessionStorage.setItem('camerasEnableFilter', this.filterEnable);
      } else {
        sessionStorage.setItem('camerasEnableFilter', "");
      }
      if (this.filterConnected?.length) {
        sessionStorage.setItem('camerasConnectedFilter', this.filterConnected);
      } else {
        sessionStorage.setItem('camerasConnectedFilter', "");
      }
    },

    updateMotion(duration) {
      this.eventDuration = duration;
      this.lastMotionCtr = 0;
      this.setProgressVal(0);
      this.setProgressLinear(true);
      this.selected[0].lastMotionDt = -1;
      this.getLastMotion(this.selected[0].vsId);
    },

    updateStreamEvents(duration) {
      this.streamEventIdx = 0;
      this.eventDuration = duration;
      while(this.streamEventIdx < this.selected.length && this.selected[this.streamEventIdx].mpIds.length <= 0) {
        this.streamEventIdx++;
        console.log("updateStreamEvents: " + this.streamEventIdx + "  " + this.selected.length)
      }
      if (this.streamEventIdx < this.selected.length) {
        this.setProgressVal(0);
        this.setProgressLinear(true);
        this.selected[this.streamEventIdx].streamEventCount = -1;
        this.getStreamEventCount(this.selected[this.streamEventIdx].mpIds[0])
      }
    },

    updateCloudStats() {
      this.cloudStatsIdx = 0;
      this.setProgressVal(0);
      this.setProgressLinear(true);
      this.selected[0].cloudGB = 0;
      this.selected[0].orphanedGB = 0;
      this.getCloudStats(this.selected[0].vsId, this.selected[0].vsCloudDays);
    },

    exportCameras() {
      console.log("Cameras::exportCameras()")
      let name = "cameras.csv";
      let exportItems = [];
      let headerString = 'Gateway, GW Connected, Cust Place, IP Address, Make, Model, Name, Firmware, Cloud Days, Enabled, Dev Connected, Stream Status, Stream Details, Last Motion, Stream Events, Cloud GB, Orphaned GB\n';
      exportItems.push(headerString);
      this.selected.forEach(item => {
        let itemString = '';
        itemString += item.devGwSerNum + ',';
        itemString += item.devGwConnected ? 'true,' : 'false,';
        itemString += '"' + item.custPlace + '",'; // Name may contain commas
        itemString += item.devAddress + ',';
        itemString += '"' + item.devMake+ '",'; // may contain commas
        itemString += '"' + item.devModel+ '",'; // may contain commas
        itemString += '"' + item.devName + '",'; // Name may contain commas
        itemString += '"' + item.devFirmware + '",'; // Firmware may contain commas
        itemString += item.vsCloudDays + ',';
        itemString += item.vsEnabled + ',';
        itemString += item.devConnected + ',';
        itemString += item.vsMpConnectedStatus + ','; 
        itemString += '"' + item.tooltip + '",';
        itemString += '"' + item.lastMotionDt  + '",'; // Date format contains comma 
        itemString += item.streamEventCount + ','; 
        itemString += item.cloudGB + ','; 
        itemString += item.orphanedGB + '\n'; 
        exportItems.push(itemString);
      });
      let blob = new Blob(exportItems, {
        type: "text/csv"
      });
      saveAs(blob, name);
    },

    prepareCameras() {
      console.log("prepareCameras");
      this.cameras = [];
      let id = 0; // ensure camera items have some difference between rows
      for (let device of this.devices) {
        for (let videoSource of device?.videoSources) {
          // if (device.gateway.connectionStatus == false && this.hideDisconnectedGateways == true) {
          //   break
          // }
          let camera = {};
          camera.id = id++;
          camera.vsId = videoSource?.id;
          camera.devAddress = device?.address;
          camera.devMake = device?.make;
          camera.devModel = device?.model;
          camera.devName = device?.videoSources.length > 1 ? device.name + "::" + videoSource.name : videoSource.name;
          camera.devFirmware = device?.firmware;
          camera.devGwSerNum = device?.gateway?.serial;
          camera.devGwConnected = device?.gateway?.connectionStatus;
          camera.devConnected = device?.connectionStatus;
          camera.vsEnabled = videoSource.enabled;
          camera.vsCloudDays = videoSource?.cloudStorageDays;
          camera.custPlace = videoSource?.place?.toHomePlaces?.customerPlace?.name;
          if (videoSource.enabled) {
            let mpIds = [];
            camera.vsMpEnabledCount = 0;
            camera.vsMpConnectedCount = 0;
            camera.resolutions = "";
            camera.tooltip = "";
            for (let mediaProfile of videoSource.mediaProfiles) {
              if (mediaProfile.streamEnabled) {
                camera.vsMpEnabledCount++;
                camera.resolutions += mediaProfile?.videoEncoderSettings?.resolution + "; "
                camera.tooltip += "Enabled: " + mediaProfile?.streamEnabled + "; "
                camera.tooltip += "Status: " + mediaProfile?.streamStatus + "; "
                camera.tooltip += "Res: " + mediaProfile?.videoEncoderSettings?.resolution + "; "
                camera.tooltip += "FPS: " + mediaProfile?.videoEncoderSettings?.fps + "; "
                camera.tooltip += "GOP: " + mediaProfile?.videoEncoderSettings?.gopSize + "; "
                camera.tooltip += "Profile: " + mediaProfile?.videoEncoderSettings?.profile + ";<br> "
                mpIds.push(mediaProfile.id);
                if (mediaProfile.streamStatus === "Connected") {
                  camera.vsMpConnectedCount++
                }
              }
              const connected = camera.vsMpConnectedCount;
              const enabled = camera.vsMpEnabledCount;
              if (connected == enabled && connected > 1) camera.vsMpConnectedStatus = "Ok";
              else if (connected == enabled && connected == 1) camera.vsMpConnectedStatus = "Add";
              else if (connected < enabled && connected >= 1) camera.vsMpConnectedStatus = "Alert";
              else camera.vsMpConnectedStatus = "Error";
            }
            ////////////////////////////////////////////////////////////
            // There is presently a bug when two enabled streams have the same resolution.
            // See ch17918.  This section detects that condition.  This section could be removed
            // once ch17918 is resolved on the backend.
            let resolutions = [];
            let duplicate = false;
            for (let mediaProfile of videoSource.mediaProfiles) {
              if (mediaProfile.streamEnabled) {
                for (let res of resolutions) {
                  if (res == mediaProfile?.videoEncoderSettings?.resolution) {
                    duplicate = true;
                    break;
                  }
                }
                if (duplicate == false) {
                  resolutions.push(mediaProfile?.videoEncoderSettings?.resolution);
                } else {
                  break;
                }
              }
            }
            if (duplicate) camera.vsMpConnectedStatus = "ch17918";
            // end of ch17918 detection
            ///////////////////////////////////////////////////////////////

            camera.mpIds = mpIds;
            camera.streamEventCount = mpIds.length > 0 ? "tbd" : "n/a";
          } else {
            // mediaProfile defaults?
            camera.mpIds = [];
            camera.streamEventCount = "n/a";
          }
          // video source defaults
          camera.lastMotionDt = "tbd";
          camera.cloudGB = "tbd";
          camera.orphanedGB = "tbd";
          this.cameras.push(camera);
        }
      }
    },

    matchLastMotion(lastMotionEvent) {
      let d = new Date(lastMotionEvent[0].serverTime)
      let cameraIndex = this.cameras.findIndex(obj => {
        return obj.vsId === lastMotionEvent[0].videoSourceID
      })
      if (cameraIndex >= 0) {
        console.log('matchLastMotion() found:' + lastMotionEvent[0].videoSourceID)
        this.cameras[cameraIndex].lastMotionDt = d.toLocaleString();
        this.$set(this.cameras, cameraIndex, this.cameras[cameraIndex]); // use $set to make array reactive
      }
    },

    matchStreamEvents(streamEventResponse) {
      let cameraIndex = this.cameras.findIndex(obj => {
        for (let ii=0; ii<obj.mpIds.length; ii++) {
          return obj.mpIds[ii] === streamEventResponse.mediaProfileID
        }
      })
      if (cameraIndex >= 0) {
        console.log('matchStreamEvents() found:' + streamEventResponse.mediaProfileID)
        this.cameras[cameraIndex].streamEventCount = streamEventResponse.count;
        this.$set(this.cameras, cameraIndex, this.cameras[cameraIndex]); // use $set to make array reactive
      }
    },

    matchCloudStats(cloudStats) {
      const vsId = cloudStats[0].videoSource.id;
      const start = DateTime.fromISO(cloudStats[0].recorded);
      let cameraIndex = this.cameras.findIndex(obj => {
        return obj.vsId === vsId;
      })
      if (cameraIndex >= 0) {
        const cloudDays = parseInt(this.cameras[cameraIndex].vsCloudDays);
        let cloudB = 0;
        let orphanedB = 0;
        cloudStats.forEach( item => {
          if (item.videoSource.id == vsId) {
            if (DateTime.fromISO(item.recorded) >= start.minus({days:cloudDays})) {
              cloudB += parseInt(item.sizeClips);
            } else {
              orphanedB += parseInt(item.sizeClips);
            }
          }
        })
        let cloudGB = Math.round(10 * cloudB /(1024*1024*1024)) / 10; // GCP storage price is Gibibytes
        let orphanedGB = Math.round(10 * orphanedB /(1024*1024*1024)) / 10;
        console.log('matchCloudStats() found:' + vsId + " index: " + cameraIndex + " " + cloudGB)
        this.cameras[cameraIndex].cloudGB = cloudGB;
        this.cameras[cameraIndex].orphanedGB = orphanedGB;
      }
    },

    getCameras() {
      console.log('/API/public/systems/cameras')
      this.app.restApi({
          method: 'get',
          url: '/API/public/systems/cameras',
          headers: {
            'Authorization': 'Bearer ' + this.app.userProfile.token
          }
        })
        .then(response => {
          console.log("Success: /API/public/systems/cameras");
          this.devices = Object.values(response.data);
          this.prepareCameras();
        })
        .catch( error => {
          console.log(error);
          alert("Failed to get cameras from QxControl cloud. error=" + error.response.status)
        });
    },

    getLastMotion(videoSourceID) {
      console.log('cameras::getLastMotion() /API/public/systems/events')
      let end = new Date();
      let start = new Date(end.getTime() - this.eventDuration*60*60*1000);
      let startIso = start.toISOString();
      let endIso = end.toISOString();

      let url = '/API/public/systems/events'
      url += "?srcIdType=videoSourceID"
      url += "&srcId=" + videoSourceID
      url += "&evtType=Motion,ObjectAction"
      url += "&startTime=" + encodeURIComponent(startIso)
      url += "&endTime=" + encodeURIComponent(endIso)
      url += "&limit=1"
      this.app.restApi({
          method: 'get',
          url: url,
          headers: {
            'Authorization': 'Bearer ' + this.app.userProfile.token
          }
        })
        .then(response => {
          console.log("cameras::getLastMotion() Success: /API/public/systems/events");
          if (response.data.length > 0) {
            const lastMotionEvent = Object.values(response.data);
            this.matchLastMotion(lastMotionEvent);
          }
        })
        .catch( error => {
          console.log("cameras::getLastMotion() error: " + error);
          // This doesn't work if I kick off multiple requests at once - need to parse by id
          if (error.response.status == 408) {
            this.selected[this.lastMotionCtr].lastMotionDt = "Timeout";
          } else {
            this.selected[this.lastMotionCtr].lastMotionDt =  "error: " + error.response.status;
          }
          this.$set(this.selected, this.lastMotionCtr, this.selected[this.lastMotionCtr]); // use $set to make array reactive
        })
        .finally(() => {
          this.lastMotionCtr++;
          this.setProgressVal(100*this.lastMotionCtr/this.selected.length);
          if (this.lastMotionCtr < this.selected.length) {
            this.selected[this.lastMotionCtr].lastMotionDt = -1;
            this.getLastMotion(this.selected[this.lastMotionCtr].vsId);
          } else {
            this.setProgressLinear(false);
            //alert("Completed finding selected last motion events");
          }
        });
    },

    getStreamEventCount(mediaProfileID) {
      console.log('cameras::getStreamEventCount() /API/public/systems/events')
      let end = new Date();
      let start = new Date(end.getTime() - this.eventDuration*60*60*1000);
      let startIso = start.toISOString();
      let endIso = end.toISOString();

      let url = '/API/public/systems/events'
      url += "?srcIdType=mediaProfileID"
      url += "&srcId=" + mediaProfileID
      url += "&evtType=StreamStatus"
      url += "&startTime=" + encodeURIComponent(startIso)
      url += "&endTime=" + encodeURIComponent(endIso)
      url += "&aggregate=true"
      this.app.restApi({
          method: 'get',
          url: url,
          headers: {
            'Authorization': 'Bearer ' + this.app.userProfile.token
          }
        })
        .then(response => {
          console.log("cameras::getStreamEventCount() Success: /API/public/systems/events ");
          this.matchStreamEvents(Object.assign(response.data));
        })
        .catch( error => {
          console.log("cameras::getStreamEventCount() error: " + error);
          // This doesn't work if I kick off multiple requests at once - need to parse by id
          if (error.response.status == 408) {
            this.selected[this.streamEventIdx].streamEventCount = "Timeout";
          } else {
            this.selected[this.streamEventIdx].streamEventCount = "error: " + error.response.status;
          }
          this.$set(this.selected, this.streamEventIdx, this.selected[this.streamEventIdx]); // use $set to make array reactive
        })
        .finally(() => {
          this.streamEventIdx++;
          while(this.streamEventIdx < this.selected.length && this.selected[this.streamEventIdx].mpIds.length <= 0) {
            this.streamEventIdx++;
          }
          this.setProgressVal(100*this.streamEventIdx/this.selected.length);
          if (this.streamEventIdx < this.selected.length) {
            this.selected[this.streamEventIdx].streamEventCount = -1;
            this.getStreamEventCount(this.selected[this.streamEventIdx].mpIds[0]);
          } else {
            this.setProgressLinear(false);
            //alert("Completed finding selected stream connection events");
          }
        });
    },

    getCloudStats(videoSourceID, cloudDays) {
      console.log('cameras::getCloudStats() /API/public/systems/recordingStats')
      const end = new Date(Date.now());
      let start = new Date(Date.now());
      cloudDays = 90; // TODO - I'm using this to quantify orphaned segments
      start.setDate(end.getDate() - cloudDays);
      let url = '/API/public/systems/recordingStats'
      url += "?srcId=" + videoSourceID
      url += "&endDate=" + end.toISOString()
      url += "&startDate=" + start.toISOString()
          console.log("cameras::getCloudStats() : " + url);
      this.app.restApi({
          method: 'get',
          url: url,
          headers: {
            'Authorization': 'Bearer ' + this.app.userProfile.token
          }
        })
        .then(response => {
          console.log("cameras::getCloudStats() Success: /API/public/systems/recordingStats");
          if (response.data.length > 0) {
            const cloudStats = Object.values(response.data);
            this.matchCloudStats(cloudStats);
          }
        })
        .catch( error => {
          console.log("cameras::getCloudStats() error: " + error);
          // This doesn't work if I kick off multiple requests at once - need to parse by id
          if (error.response.status == 408) {
            this.selected[this.cloudStatsIdx].cloudGB = "Timeout";
          } else {
            this.selected[this.cloudStatsIdx].cloudGB =  "error: " + error.response.status;
          }
        })
        .finally(() => {
          this.cloudStatsIdx++;
          this.setProgressVal(100*this.cloudStatsIdx/this.selected.length);
          if (this.cloudStatsIdx < this.selected.length) {
            this.selected[this.cloudStatsIdx].cloudGB = 0;
            this.getCloudStats(this.selected[this.cloudStatsIdx].vsId, this.selected[this.cloudStatsIdx].vsCloudDays);
          } else {
            this.setProgressLinear(false);
            //alert("Completed finding selected last motion events");
          }
        });
    },

  }
}
</script>